/**
 * What the table says: seat summaries, the turn note, team names and the
 * scoreboard's reading of who leads.
 *
 * Kept apart from the components so it can be tested without a DOM - these
 * sentences are how a screen-reader player learns the state of the table, so
 * they deserve the same scrutiny as the rules.
 */

import {
    type GameView,
    type PlayerView,
    PURE_BOOK_BONUS,
    WILD_BOOK_BONUS,
    getMeldValue,
    isPureBook,
    isWildBook
} from "@ryzun/haf-saloon-game-engine";
import type { TeamColor, TeamPresentation } from "../server/protocol.js";
import { getRankWord } from "./cards.js";
import type { SeatHolder } from "./lobby.js";
import type { TurnOptions } from "./play.js";

export type SeatRole = "opponent" | "partner" | "self";

export interface SeatSummaryInput {
    readonly player: PlayerView;
    readonly role: SeatRole;
    readonly clock: number;
    readonly isTurn: boolean;
    readonly isDealer: boolean;
    readonly holder: SeatHolder;
}

export interface TurnNote {
    readonly text: string;
    /** Whether the note announces something the player can do, which earns it emphasis. */
    readonly actionable: boolean;
}

/** What a team's table is worth right now, before anything held is counted against it. */
export interface TableEstimate {
    readonly bookBonus: number;
    readonly tableValue: number;
    readonly total: number;
}

const ROLE_WORDS: Readonly<Record<SeatRole, string>> = {
    opponent: "opponent",
    partner: "your partner",
    self: "you"
};

/** A person playing their own seat goes without saying. */
const HOLDER_WORDS: Readonly<Record<SeatHolder, string | null>> = {
    away: "away, a bot is playing for them",
    bot: "a bot",
    player: null
};

/** The color a team was dealt, falling back to blue for a team the server never named. */
export function getTeamColor(teamColors: readonly TeamPresentation[], teamId: string): TeamColor {
    return teamColors.find((team) => team.teamId === teamId)?.color ?? "blue";
}

/**
 * The engine's team ids are "team-1" and "team-2". Nobody should have to work
 * out which is theirs from a color pip, so teams are named by who sits at them.
 */
export function getTeamName(view: GameView, teamId: string): string {
    const members = view.players.filter((player) => player.teamId === teamId);
    const mine = members.some((player) => player.id === view.viewerId);

    return mine ? "Your team" : members.map((player) => player.name).join(" & ");
}

/**
 * A player in their Foot has an empty Hand - the engine only flips `inFoot`
 * once the Hand runs out - so the pile being played from has to be chosen
 * before the count is read, or everyone on their Foot shows as holding zero.
 */
export function getHeldCount(player: PlayerView): number {
    return player.inFoot ? player.footCount : player.handCount;
}

/** The accessible name for a seat: who, where, and what they are holding. */
export function describeSeat(input: SeatSummaryInput): string {
    const held = getHeldCount(input.player);

    return [
        `${input.player.name}, ${ROLE_WORDS[input.role]}, ${input.clock} o'clock`,
        HOLDER_WORDS[input.holder],
        input.player.inFoot
            ? `on their Foot, ${held} cards left`
            : `${held} cards in hand, Foot of ${input.player.footCount} still face down`,
        input.isTurn ? "taking their turn" : null,
        input.isDealer ? "dealer" : null
    ].filter((part) => part !== null).join(", ");
}

/** The line that says whose move it is and what kind of move it should be. */
export function describePhase(options: TurnOptions): string {
    if (!options.isYourTurn) {
        return "Waiting for the other players";
    }

    return options.isDrawPhase
        ? "Draw two from the stock, or take the pile with a meld"
        : "Play what you can, then click the discard pile to end your turn";
}

/**
 * Melding is done by clicking the table, so the note says where the cards can
 * go rather than offering a second place to send them from.
 *
 * Somewhere to put the cards outranks any reason they are not a new meld: a
 * lone wild is not a meld on its own, but if the team has a meld it can join,
 * saying so is the useful half. The two never collide outside the play phase,
 * where nothing is addable anyway.
 */
export function describeTurnNote(
    options: TurnOptions,
    roundMinimum: number,
    stagedCount: number,
    selectedCount: number
): TurnNote {
    const opensRound = options.team !== null && !options.team.inGame;

    if (options.addableRanks.length > 0) {
        const ranks = options.addableRanks.map((rank) => getRankWord(rank, true)).join(" or ");

        return { text: `Click your team's ${ranks} on the table to add these cards.`, actionable: true };
    }

    if (options.playHint !== null) {
        return { text: options.playHint, actionable: true };
    }

    if (options.canStageMeld) {
        const purpose = opensRound ? "set these aside for your opener" : "put these down";

        return { text: `Click "New meld" in front of you to ${purpose}.`, actionable: true };
    }

    if (opensRound && stagedCount === 0) {
        return {
            text: `Your team needs ${roundMinimum} points to get in - build each meld, then lay them down together.`,
            actionable: false
        };
    }

    return { text: `${selectedCount} selected.`, actionable: false };
}

/** Announced assertively, so it has to carry the next step, not just the news. */
export function getTurnAlert(options: TurnOptions): string | null {
    if (!options.isYourTurn) {
        return null;
    }

    return options.isDrawPhase ? "Your turn. Draw to begin." : "Your turn - play or discard.";
}

/**
 * Teams holding the top score. Ties share the crown: the moment a round or the
 * game ends is not the time to pretend one of two equal scores edged the other
 * out. Nobody leads while every score is still at or below zero.
 */
export function getLeadingTeamIds(view: GameView): readonly string[] {
    const best = Math.max(...view.teams.map((team) => team.score));

    if (best <= 0) {
        return [];
    }

    return view.teams.filter((team) => team.score === best).map((team) => team.id);
}

/**
 * The round in progress, scored from the table alone: book bonuses and the face
 * value of every meld (§6). Cards still held are hidden from everyone but their
 * owner, so the deductions they will bring are left out - which is why this is
 * an estimate. Null once the round has been scored, when the real total is in.
 */
export function getTableEstimate(view: GameView, teamId: string): TableEstimate | null {
    const team = view.teams.find((entry) => entry.id === teamId);

    if (view.status !== "in-round" || team === undefined) {
        return null;
    }

    const bookBonus = (team.melds.filter(isPureBook).length * PURE_BOOK_BONUS)
        + (team.melds.filter(isWildBook).length * WILD_BOOK_BONUS);
    const tableValue = team.melds.reduce((total, meld) => total + getMeldValue(meld), 0);

    return { bookBonus, tableValue, total: bookBonus + tableValue };
}

/** A team's total for one round, or null when that round has not been scored. */
export function getRoundTotal(view: GameView, round: number, teamId: string): number | null {
    const scored = view.scores.find((entry) => entry.round === round);

    return scored?.teams.find((team) => team.teamId === teamId)?.total ?? null;
}

/**
 * How long a game has run, as a clock reads it: minutes and seconds, with
 * hours in front once there are any. Never negative, whatever the clocks say.
 */
export function formatElapsed(ms: number): string {
    const seconds = Math.floor(Math.max(ms, 0) / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const pad = (value: number): string => String(value).padStart(2, "0");

    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds % 60)}`
        : `${minutes}:${pad(seconds % 60)}`;
}
